import React, { useState } from 'react';
import BuildingMap, { BUILDING_DOTS_MIN_ZOOM } from '../../../HouseMap/BuildingMap';
import { BuildingWithConvertedAddresses, LatLng } from '../../../utils/types';

interface FlowMapProps {
  // Seeded once from the live landing-map centroid / zoom.
  mapCenter: LatLng;
  mapZoom: number;
  countryOfFeedContent: string;
  // Address step passes true (display-only). Building step leaves it false and
  // wires `selectBuilding` to pick the building (without opening the modal).
  disableBuildingClick?: boolean;
  // Address step passes true so the map is a frozen display (no pan/zoom/rotate).
  staticMap?: boolean;
  // Address step passes true to hide Mapbox street/place/POI text labels.
  hideLabels?: boolean;
  selectBuilding?: (building: BuildingWithConvertedAddresses) => void;
  // Building step: a click started resolving another building's address.
  onBuildingPickStart?: () => void;
  // Building step: that click's building has no address, or its lookup failed.
  onBuildingPickFailed?: () => void;
  // Centered element rendered over the map (house pin or "My place" callout).
  centerOverlay?: React.ReactNode;
  // Building step: highlight the building at this coordinate and anchor
  // `addressCallout` to it (instead of the viewport-centered `centerOverlay`).
  highlightAddressAt?: LatLng | null;
  addressCallout?: React.ReactNode;
  // When true, recenter to `mapCenter` changes instantly (no fly animation) so
  // moving from the address step to the picked building has no slow pan.
  recenterInstant?: boolean;
  // Framing zoom applied on an instant recenter; defaults to the flow's initial
  // building-dots zoom.
  recenterZoom?: number;
}

const noop = () => {};

/**
 * Wraps the shared BuildingMap with the defaults the Add-my-place flow needs:
 * a simplified map (no buy/sell listing markers, like hearts, or building dots,
 * via `simplifiedMap`), no geolocate/zoom controls (per Figma), gradients +
 * centered overlay, and the Mapbox attribution kept visible.
 */
const FlowMap = ({
  mapCenter,
  mapZoom,
  countryOfFeedContent,
  disableBuildingClick,
  staticMap,
  hideLabels,
  selectBuilding,
  onBuildingPickStart,
  onBuildingPickFailed,
  centerOverlay,
  highlightAddressAt,
  addressCallout,
  recenterInstant,
  recenterZoom,
}: FlowMapProps) => {
  // Default to at least the building-dots zoom (the `building-clickable-dots`
  // layer's minzoom) so dots are visible on open, while preserving a more
  // zoomed-in landing state if the user had one.
  const initialZoom = Math.max(mapZoom, BUILDING_DOTS_MIN_ZOOM);
  // Local, throwaway map state — the flow never needs to read these back.
  const [lastMapBoundaryBoxCenter, setLastMapBoundaryBoxCenter] = useState<LatLng | null>(mapCenter);
  const [lastZoomLevel, setLastZoomLevel] = useState<number | null>(initialZoom);
  const [country, setCountry] = useState<string>(countryOfFeedContent);
  const [triggeredFromSearch, setTriggeredFromSearch] = useState(false);

  return (
    <div className="amp-flow-map">
      <BuildingMap
        mapCenter={mapCenter}
        selectedBuilding={null}
        triggeredFromSearch={triggeredFromSearch}
        searchResultBounds={null}
        feedIsOpen={false}
        mobileFeedIsOpen={false}
        countryOfFeedContent={country}
        lastMapBoundaryBoxCenter={lastMapBoundaryBoxCenter}
        lastZoomLevel={lastZoomLevel}
        initialMapZoom={initialZoom}
        isLandingPage
        hideGeolocateControl
        simplifiedMap
        hideLabels={hideLabels}
        disableBuildingClick={disableBuildingClick}
        staticMap={staticMap}
        addressHighlightAt={highlightAddressAt}
        addressCallout={addressCallout}
        recenterInstant={recenterInstant}
        recenterZoom={recenterInstant ? (recenterZoom ?? initialZoom) : undefined}
        selectBuilding={selectBuilding ?? noop}
        onBuildingPickStart={onBuildingPickStart}
        onBuildingPickFailed={onBuildingPickFailed}
        setShowFeed={noop}
        setTriggeredFromSearch={setTriggeredFromSearch}
        setCountryOfFeedContent={(value) => setCountry(value)}
        setLastMapBoundaryBoxCenter={setLastMapBoundaryBoxCenter}
        setLastZoomLevel={setLastZoomLevel}
      />
      <div className="amp-flow-map__gradient amp-flow-map__gradient--top" aria-hidden="true" />
      <div className="amp-flow-map__gradient amp-flow-map__gradient--bottom" aria-hidden="true" />
      {centerOverlay && <div className="amp-flow-map__center">{centerOverlay}</div>}
    </div>
  );
};

export default FlowMap;
